import { DatePipe } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { DialogBase, DialogOptions } from '@context/frontend/common';
import { ContentRowComponent } from '@context/frontend/content';
import { AvatarComponent, UserNamePipe } from '@context/frontend/user';
import {
	ApplicationType,
	Chemical,
	ChemicalRevision,
	ChemicalUsageLog,
} from '@context/shared/types/chemical';
import { Content } from '@context/shared/types/common';
import { User } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiLoader, TuiScrollbar, TuiInput } from '@taiga-ui/core';
import { DocumentReference, getDoc } from 'firebase/firestore';
import { ChemicalReiBadgeComponent } from '../../components';

@Component({
	imports: [
		TranslocoModule,
		TuiInput,
		TuiLoader,
		TuiScrollbar,
		ContentRowComponent,
		DatePipe,
		ChemicalReiBadgeComponent,
		AvatarComponent,
		UserNamePipe,
	],
	selector: 'ctx-chemical-usage-log-details-dialog',
	templateUrl: 'chemical-usage-log-details-dialog.component.html',
	styleUrl: 'chemical-usage-log-details-dialog.component.scss',
})
export class ChemicalUsageLogDetailsDialogComponent
	extends DialogBase<
		null,
		{ usageLog: ChemicalUsageLog; showInternalNote?: boolean }
	>
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'chemical.usage-log.view-details',
		size: 'm',
	} as DialogOptions;

	readonly loading = signal(true);

	readonly chemical = signal<Chemical | null>(null);
	readonly chemicalRevision = signal<ChemicalRevision | null>(null);
	readonly applicationType = signal<ApplicationType | null>(null);
	readonly crops = signal<Content[]>([]);
	readonly createdByUser = signal<User | null>(null);
	readonly canShowInternalNote = () =>
		this.context.data.showInternalNote === true;
	readonly usageLogNote = () => this.context.data.usageLog.note?.trim();

	async ngOnInit() {
		this.loading.set(true);

		try {
			await this.loadAllData();
		} catch (error) {
			console.error('Error loading usage log details:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private async loadAllData() {
		const usageLog = this.context.data.usageLog;

		const promises: Promise<any>[] = [
			getDoc(usageLog.chemical as DocumentReference<Chemical>).catch(
				(error) => {
					console.error('Error loading chemical:', error);
				},
			),
			getDoc(
				usageLog.chemicalRevision as DocumentReference<ChemicalRevision>,
			).catch((error) => {
				console.error('Error loading chemical revision:', error);
			}),
			getDoc(
				usageLog.applicationType as DocumentReference<ApplicationType>,
			).catch((error) => {
				console.error('Error loading application type:', error);
			}),
			...usageLog.crops.map((cropRef) =>
				getDoc(cropRef as DocumentReference<Content>).catch((error) => {
					console.error('Error loading crop:', error);
				}),
			),
		];

		// Load createdBy user if it exists
		if (usageLog.createdBy) {
			promises.push(
				getDoc(usageLog.createdBy as DocumentReference<User>).catch(
					(error) => {
						console.error('Error loading created by user:', error);
					},
				),
			);
		}

		const results = await Promise.all(promises);

		const chemicalDoc = results[0];
		const revisionDoc = results[1];
		const applicationTypeDoc = results[2];
		const cropDocs = results.slice(3, 3 + usageLog.crops.length);
		const createdByDoc = usageLog.createdBy
			? results[results.length - 1]
			: null;

		this.chemical.set(chemicalDoc.data() as Chemical);
		this.chemicalRevision.set(revisionDoc.data() as ChemicalRevision);
		this.applicationType.set(applicationTypeDoc.data() as ApplicationType);

		const loadedCrops = cropDocs
			.filter((doc) => doc.exists())
			.map((doc) => doc.data() as Content);
		this.crops.set(loadedCrops);

		if (createdByDoc?.exists()) {
			this.createdByUser.set(createdByDoc.data() as User);
		}
	}

	readonly appliedAtDate = () => {
		const log = this.context.data.usageLog;
		return log.appliedAt?.toDate() ?? null;
	};

	readonly reiDate = () => {
		const log = this.context.data.usageLog;
		const appliedAt = this.appliedAtDate();
		if (!appliedAt) return null;

		const reiHours = log.reiHours;
		const date = new Date(appliedAt);
		date.setHours(date.getHours() + reiHours);
		return date;
	};
}
